import { useState, useCallback } from "react";

interface ConfirmationDialogOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: "danger" | "warning" | "info";
}

interface ConfirmationDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  type: "danger" | "warning" | "info";
  onConfirm: (() => void) | null;
  onCancel: (() => void) | null;
}

const useConfirmationDialog = () => {
  const [dialogState, setDialogState] = useState<ConfirmationDialogState>({
    isOpen: false,
    title: "Confirm Action",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    type: "info",
    onConfirm: null,
    onCancel: null,
  });

  const showConfirmation = useCallback(
    (
      options: ConfirmationDialogOptions,
      onConfirm: () => void,
      onCancel?: () => void
    ) => {
      setDialogState({
        isOpen: true,
        title: options.title || "Confirm Action",
        message: options.message,
        confirmText: options.confirmText || "Confirm",
        cancelText: options.cancelText || "Cancel",
        type: options.type || "info",
        onConfirm,
        onCancel: onCancel || null,
      });
    },
    []
  );

  const hideDialog = useCallback(() => {
    setDialogState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  }, []);

  const handleConfirm = useCallback(() => {
    setDialogState((prev) => {
      if (prev.onConfirm) {
        prev.onConfirm();
      }
      return {
        ...prev,
        isOpen: false,
      };
    });
  }, []);

  const handleCancel = useCallback(() => {
    setDialogState((prev) => {
      if (prev.onCancel) {
        prev.onCancel();
      }
      return {
        ...prev,
        isOpen: false,
      };
    });
  }, []);

  return {
    dialogState,
    showConfirmation,
    hideDialog,
    handleConfirm,
    handleCancel,
  };
};

export default useConfirmationDialog;
